import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { toast } from "react-toastify";
import { findProductsByBatchNumber } from "../../services/productService";
import useStyles from "./styles";

const BatchNumberFinder = forwardRef(
  ({ defaultValue, company, onChange, onSelectProduct }, ref) => {
    const [value, setValue] = useState(defaultValue);
    const [products, setProducts] = useState([]);
    const [debouncedValue, setDebouncedValue] = useState(defaultValue);
    const [message, setMessage] = useState(
      "Please type batch number to find the product"
    );
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const tableBodyRef = useRef(null);
    const inputRef = useRef(null);

    const activeSearchId = useRef(0);

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
        inputRef.current?.select();
      },
    }));

    const clearAll = () => {
      setProducts([]);
      setMessage("Please type batch number to find the product");
      setSelectedIndex(-1);
      setDebouncedValue("");
      activeSearchId.current++;
    };

    const searchProducts = async () => {
      if (debouncedValue !== value) return;
      const currentSearchId = ++activeSearchId.current;
      setProducts([]);
      setMessage(`Searching products by "${debouncedValue}" ...`);

      try {
        const response = await findProductsByBatchNumber(
          debouncedValue,
          company
        );

        // 🟢 Ignore old responses
        if (currentSearchId !== activeSearchId.current) return;

        if (response?.status === "success") {
          const sortedProducts = response.data.sort((a, b) => {
            const search = debouncedValue.toLowerCase();

            const aBatch = a.batch.toLowerCase();
            const bBatch = b.batch.toLowerCase();

            if (aBatch === search) return -1;
            if (bBatch === search) return 1;

            if (aBatch.startsWith(search)) return -1;
            if (bBatch.startsWith(search)) return 1;

            return aBatch.localeCompare(bBatch);
          });
          const limitedProducts = sortedProducts.slice(0, 100);
          setProducts(limitedProducts);
          setSelectedIndex(limitedProducts.length > 0 ? 0 : -1);
          setMessage(
            sortedProducts.length > 0
              ? `${sortedProducts.length} products found ${sortedProducts.length > 100 ? '(Showing top 100)' : ''}`
              : "No products found"
          );
        } else {
          toast.error(response?.error || "Failed to fetch products");
        }
      } catch (err) {
        console.error("Search error:", err);
        if (currentSearchId === activeSearchId.current) {
          toast.error("Error fetching products");
        }
      } finally {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };

    const handleSelect = (item) => {
      onSelectProduct(item);
      clearAll();
      setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter" && products.length === 0 && value.length >= 3) {
        e.preventDefault();
        searchProducts();
        return;
      }

      if (products.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (selectedIndex === -1 && products.length > 0) {
            setSelectedIndex(0);
          } else {
            setSelectedIndex((prev) =>
              prev < products.length - 1 ? prev + 1 : 0
            );
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : products.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < products.length) {
            handleSelect(products[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          clearAll();
          setTimeout(() => inputRef.current?.focus(), 0);
          break;
        default:
          break;
      }
    };

    // Scroll to selected row
    useEffect(() => {
      if (selectedIndex >= 0 && tableBodyRef.current) {
        const selectedRow = tableBodyRef.current.children[selectedIndex];
        if (selectedRow) {
          selectedRow.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }
    }, [selectedIndex]);

    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedValue(value);
      }, 250);
      return () => clearTimeout(timer);
    }, [value]);

    useEffect(() => {
      if (value.trim().length < 3) {
        clearAll();
      }
    }, [value]);

    useEffect(() => {
      if (debouncedValue && debouncedValue.length > 2) {
        searchProducts();
      } else {
        setProducts([]);
      }
    }, [debouncedValue]);

    // Reset when defaultValue changes
    useEffect(() => {
      clearAll();
      setValue(defaultValue || "");
    }, [defaultValue]);

    return (
      <div style={{ maxHeight: 300 }}>
        <input
          ref={inputRef}
          type="text"
          value={value} // ✅ controlled input
          onChange={(e) => {
            const newValue = e.target.value;
            onChange(newValue);
            setValue(newValue);

            // clear old results immediately
            setProducts([]);
            setSelectedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          className="form-control"
          placeholder="Type batch number..."
        />
        <div style={{ maxHeight: 250, overflow: "auto" }}>
          <div
            style={{ marginTop: 10 }}
            className={products.length === 0 ? "messageRed" : "messageBlack"}
          >
            {message}
          </div>
          <table className="table table-striped table-sm">
            <thead>
              <tr>
                <th style={{ width: 80 }}></th>
                <th style={{ width: 100 }}>Batch</th>
                <th style={{ width: 100 }}>SKU</th>
                <th style={{ width: 80 }}>MRP</th>
                <th style={{ width: 100 }}>Expiry</th>
              </tr>
            </thead>
            <tbody ref={tableBodyRef}>
              {products.map((item, index) => (
                <tr
                  key={index}
                  tabIndex={0}
                  className={selectedIndex === index ? "bg-primary text-white" : ""}
                  style={{
                    backgroundColor:
                      selectedIndex === index ? "#0d6efd" : "transparent",
                    color: selectedIndex === index ? "white" : "inherit",
                    fontWeight: selectedIndex === index ? "bold" : "normal",
                    outline: selectedIndex === index ? "2px solid #0a58ca" : "none",
                    cursor: "pointer",
                  }}
                  onMouseDown={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onKeyDown={handleKeyDown}
                >
                  <td>
                    <button
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleSelect(item);
                      }}
                      className="btn btn-sm btn-success"
                    >
                      Select
                    </button>
                  </td>
                  <td>
                    {item.obatch
                      ? `${item.batch} (${item.obatch})`
                      : item.batch}
                  </td>
                  <td>{item.sku}</td>
                  <td>{item.mrp}</td>
                  <td>{item.expiryDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);
export default BatchNumberFinder;
